import { buildApiUrl } from "../../utils/api";
import { getCookie, setCookie, deleteCookie } from "../../utils/cookies";

export const createAuthSlice = (set, get) => ({
  user: null,
  auth_loading: false,
  auth_error: null,
  isAuthenticated: false,
  profile_loading: false,
  profile_error: null,

  login: async (credentials) => {
    set({ auth_loading: true, auth_error: null });
    try {
      const response = await fetch(buildApiUrl("/auth/login"), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });
      const data = await response.json();
      
      if (data.success) {
        const token = data.token || data.data?.token || data.access_token || data.data?.access_token;
        set({
          user: data.user || data.data,
          isAuthenticated: true,
          auth_loading: false,
          auth_error: null
        });
        if (token) {
          setCookie('authToken', token, 7);
          // Fetch wishlist after login
          setTimeout(() => {
            if (typeof get().fetchWishlist === 'function') get().fetchWishlist();
            if (typeof get().mergeGuestCartOnLogin === 'function') get().mergeGuestCartOnLogin();
            else if (typeof get().fetchCart === 'function') get().fetchCart();
          }, 100);
        } else {
          // Token missing — cookie not set
        }
        return data;
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (error) {
      set({ auth_error: error.message, auth_loading: false });
      throw error;
    }
  },

  logout: () => {
    // Delete cookie first so clearCart API call is skipped
    deleteCookie('authToken');
    // Reset all cart, auth, and wishlist state cleanly
    set({
      user: null,
      isAuthenticated: false,
      auth_error: null,
      cart: [],
      cart_count: 0,
      cart_total: 0,
      cart_gst_total: 0,
      cart_error: null,
      wishlist: [],
      wishlist_count: 0,
      wishlist_error: null,
    });
  },

  register: async (userData) => {
    set({ auth_loading: true, auth_error: null });
    try {
      const response = await fetch(buildApiUrl("/auth/register"), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      const data = await response.json();
      
      if (data.success) {
        const token = data.token || data.data?.token || data.access_token || data.data?.access_token;
        set({
          user: data.user || data.data,
          isAuthenticated: true,
          auth_loading: false
        });
        if (token) {
          setCookie('authToken', token, 7);
          // Fetch wishlist after registration
          setTimeout(() => {
            if (typeof get().fetchWishlist === 'function') get().fetchWishlist();
            if (typeof get().mergeGuestCartOnLogin === 'function') get().mergeGuestCartOnLogin();
            else if (typeof get().fetchCart === 'function') get().fetchCart();
          }, 100);
        } else {
          console.warn('[Auth] No token in register response — cookie NOT set');
        }
        return data;
      } else {
        throw new Error(data.message || 'Registration failed');
      }
    } catch (error) {
      set({ auth_error: error.message, auth_loading: false });
      console.error('Registration error:', error);
      throw error;
    }
  },

  checkAuth: async () => {
    const token = getCookie('authToken');
    if (!token) return;

    // Clear corrupted tokens left over from old encodeURIComponent storage
    if (token.includes('%')) {
      deleteCookie('authToken');
      set({ isAuthenticated: false, user: null, auth_loading: false });
      return;
    }

    set({ auth_loading: true });
    try {
      const response = await fetch(buildApiUrl('/api/v1/customer/profile'), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (response.status === 401) {
        // Token is genuinely invalid/expired — force re-login
        deleteCookie('authToken');
        set({ isAuthenticated: false, user: null, auth_loading: false });
        return;
      }

      if (response.ok && data.success !== false) {
        const profile = data.data?.customer || data.data || data.user || data.profile || data;
        set({ user: profile, isAuthenticated: true, auth_loading: false });
      } else {
        // API error but NOT a 401 — keep the token, don't log out
        set({ isAuthenticated: true, auth_loading: false });
      }
    } catch {
      // Network error — keep the user logged in, don't delete the token
      set({ isAuthenticated: true, auth_loading: false });
    }
  },

  fetchProfile: async () => {
    const token = getCookie('authToken');
    if (!token) {
      set({ profile_error: 'Please login to view your profile' });
      return null;
    }

    set({ profile_loading: true, profile_error: null });
    try {
      const response = await fetch(buildApiUrl('/api/v1/customer/profile'), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Failed to fetch profile');
      }

      const profile = data.data?.customer || data.data || data.user || data.profile || data;
      set({
        user: profile,
        isAuthenticated: true,
        profile_loading: false,
        profile_error: null,
      });
      return profile;
    } catch (error) {
      set({ profile_error: error.message, profile_loading: false });
      return null;
    }
  },

  updateProfile: async (profileData) => {
    const token = getCookie('authToken');
    if (!token) {
      set({ profile_error: 'Please login to update your profile' });
      throw new Error('Please login to update your profile');
    }

    set({ profile_loading: true, profile_error: null });
    try {
      const response = await fetch(buildApiUrl('/api/v1/customer/profile'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });
      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Failed to update profile');
      }

      const updatedProfile = data.data?.customer || data.data || data.user || data.profile || {
        ...get().user,
        ...profileData,
      };
      set({
        user: updatedProfile,
        isAuthenticated: true,
        profile_loading: false,
        profile_error: null,
      });
      return updatedProfile;
    } catch (error) {
      set({ profile_error: error.message, profile_loading: false });
      throw error;
    }
  },
});
